import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

/**
 * One row of How to approach: a label and a value (CT-5714). A row with a source
 * is a full-width `Button` named "Open source: <label> <value>" that opens the
 * trait or insight it came from; a row without one is plain text.
 *
 * `collapsed` (the card folded to one line): the label at its own width and the
 * value cut short.
 */
const HowToApproachRow = memo(function HowToApproachRow({ field, collapsed = false, onOpenSource }) {
  const { t } = useTranslation('unified-profiles');

  const handleOpen = useCallback(() => onOpenSource(field.source), [onOpenSource, field.source]);

  const content = (
    <span className="flex min-w-0 items-baseline gap-3 text-left text-sm">
      <span className={collapsed ? 'shrink-0 font-medium text-content' : 'w-20 shrink-0 text-content-muted'}>
        {field.label}
      </span>
      <span className={collapsed ? 'min-w-0 truncate text-content-secondary' : 'min-w-0 text-content'}>
        {field.value}
      </span>
    </span>
  );

  if (!field.source || !onOpenSource) {
    return <div data-testid="approach-row" className="min-w-0 px-2 py-1">{content}</div>;
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleOpen}
      aria-label={t('profile.approach.openSource', { label: field.label, value: field.value })}
      className="!h-auto w-full min-w-0 !justify-start px-2 py-1"
    >
      {content}
    </Button>
  );
});

export default HowToApproachRow;
